import {JackpotBetsOptInDialogComponent} from "../../components/jackpot-bets/opt-in-dialog/jackpot-bets-opt-in-dialog.component";
import {ServiceBase} from "../ServiceBase";
import {IJackpotBetParticipationData, IJackpotBetsService, JackpotBetsOptStatusType} from "./jackpot-bets.service.interface";
import {DialogButtonClickHandleResultEnum} from "../dialog/dialog.service.interface";
import {SessionStorageKeys} from "../storage/session-storage-keys";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {OrbitBroadcastChannel} from "../../../utils/orbit-broadcast-channel";
import {makeObservable, observable, runInAction} from "mobx";
import {IJackpotBetsStrategy} from "./strategies/jackpot-bets-strategy.interface";
import {VoidJackpotBetsStrategy} from "./strategies/void-jackpot-bets.strategy";
import {AuthorizedJackpotBetsStrategy} from "./strategies/authorized-jackpot-bets.strategy";
import {AnonymousJackpotBetsStrategy} from "./strategies/anonymous-jackpot-bets.strategy";
import {IJackpotBetsInfoViewModel} from "./models/jackpot-bets-info.view-model.interface";
import {VoidJackpotBetsInfoModel} from "./models/void-jackpot-bets-info.model";
import {JackpotBetsController_GetParticipationDataResponse} from "../api/api.service";
import {
    JACKPOT_BET_ANALYTICS_CATEGORY,
    JACKPOT_BET_TECH_EVENTS_CATEGORY
} from "../../../common/const/jackpot-bets_consts";
import {FeatureToggleStatusEnum} from "../ab-testing/feature-toggle.view-model.interface";

interface IJackpotStatusChangedMessagePayload {
    optInStatus: JackpotBetsOptStatusType
}

export type JackpotBetsBroadcastMessage =
    | {type: 'opt-in-status-changed', payload: IJackpotStatusChangedMessagePayload};


export class JackpotBetsService extends ServiceBase implements IJackpotBetsService {

    constructor(services: IServiceFactoryExtended) {
        super(services);
        makeObservable(this);

        this._broadcastChannel = new OrbitBroadcastChannel<JackpotBetsBroadcastMessage>('jackpot-bets', this.services);
        this._broadcastChannel.subscribe(this._handleBroadcastChannelMessage)

        this.reaction(() => this.services.user.isAuthorized,
            () => {
                this._optedOutUsersNotInABPromise = this._optOutUserNotInAB()
                    .catch(err => {
                        this.services.logger.error("Failed to reject opted-in users outside AB", err);
                        this.sendJackpotBetTechnicalEvent("opted_out_user_not_ab_failed", "src_opted_out_ab", JSON.stringify(err));
                    });
            }, {
            fireImmediately: true
        });

        this.reaction(() => this.services.user.isAuthorized,
            (isAuth) => {
                if (!this.isEnabled) {
                    return;
                }
                runInAction(() => {
                    this._currentParticipationData = null;
                    this.services.sessionStorage.removeItem(this._getNewJBParticipationDataSessionStorageKey());
                });

                const betsInfo = this._currentJackpotBetsStrategy.jackpotBetsInfo;
                this._currentJackpotBetsStrategy.dispose();
                if (isAuth) {
                    this._currentJackpotBetsStrategy = new AuthorizedJackpotBetsStrategy(services, betsInfo);
                } else {
                    this._currentJackpotBetsStrategy = new AnonymousJackpotBetsStrategy(services, betsInfo);
                }
            }, {
            fireImmediately: true
        });
    }

    private _optedOutUsersNotInABPromise: Promise<void>;

    @observable.ref
    private _currentJackpotBetsStrategy: IJackpotBetsStrategy = new VoidJackpotBetsStrategy();
    private get currentJackpotBetsStrategy(): IJackpotBetsStrategy {
        if(this.isEnabled) {
            return this._currentJackpotBetsStrategy;
        }

        return new VoidJackpotBetsStrategy();
    }

    private readonly _broadcastChannel: OrbitBroadcastChannel<JackpotBetsBroadcastMessage>;

    get isEnabled(): boolean {
        return this.services.abTestingFeatureDataProvider.jackpotBet.isEnabled;
    }

    get jackpotsInfo(): IJackpotBetsInfoViewModel {
        return this.currentJackpotBetsStrategy.jackpotBetsInfo ?? new VoidJackpotBetsInfoModel();
    }

    private _handleBroadcastChannelMessage = (event: JackpotBetsBroadcastMessage) => {
        switch (event.type) {
            case "opt-in-status-changed":
                this.optInStatus = event.payload.optInStatus;
                break;
        }
    }

    @observable.ref
    private _currentParticipationData: JackpotBetsController_GetParticipationDataResponse | null = null;

    private setCurrentParticipationData(data: Partial<JackpotBetsController_GetParticipationDataResponse>): void {
        runInAction(() => {
            this._currentParticipationData = {
                ...this._currentParticipationData,
                ...data
            };

            this.services.sessionStorage.setJson(this._getNewJBParticipationDataSessionStorageKey(), this._currentParticipationData);
        });
    }

    private _getNewJBParticipationDataSessionStorageKey(): string {
        return `${SessionStorageKeys.jackpotBetsParticipationData}_${this.services.user.cid}`;
    }

    get optInStatus(): JackpotBetsOptStatusType {
        return this._currentParticipationData?.OptStatus ?? JackpotBetsOptStatusType.Unknown;
    }

    private set optInStatus(value: JackpotBetsOptStatusType) {
        let participationData = this.services.sessionStorage.getJson<JackpotBetsController_GetParticipationDataResponse>(this._getNewJBParticipationDataSessionStorageKey());
        participationData.OptStatus = value;
        this.setCurrentParticipationData({
            OptStatus: value
        });
    }

    get participationFee(): number {
        return this._currentParticipationData?.ParticipationFee ?? 0;
    }

    async getParticipationData(): Promise<IJackpotBetParticipationData> {
        await this._optedOutUsersNotInABPromise;
        const participationData = await this._readParticipationDataFromServer();
        if (participationData) {
            return {
                optInStatus: participationData.OptStatus,
                participationFee: participationData.ParticipationFee
            };
        }

        return {
            optInStatus: JackpotBetsOptStatusType.NotAvailable,
            participationFee: participationData.ParticipationFee
        };
    }

    async getOptInStatus(): Promise<JackpotBetsOptStatusType> {
        const participationData = await this._readParticipationDataFromServer();
        if (participationData) {
            return participationData.OptStatus;
        }

        return JackpotBetsOptStatusType.NotAvailable;
    }

    private async _readParticipationDataFromServer(): Promise<JackpotBetsController_GetParticipationDataResponse | null> {
        if (!this.services.user.isAuthorized) {
            return null;
        }

        if (this._currentParticipationData) {
            return this._currentParticipationData;
        }

        try {
            const participationData = await this.services.api.jackpotBetsController.getParticipationData();
            this.setCurrentParticipationData(participationData);
            return participationData;
        } catch (error) {
            this.services.logger.error("Failed to get jackpot bets participation data", error);
            return null;
        }
    }

    private async _optOutUserNotInAB(): Promise<void> {
        if (!this.services.user.isAuthorized) {
            return;
        }

        const isEnabledByAB =
            this.services.configuration.servicesConfigs.JackpotBets.featureToggle === FeatureToggleStatusEnum.EnabledByAB;

        const optInStatus: JackpotBetsOptStatusType = await this.getOptInStatus();

        const shouldOptOut =
            isEnabledByAB &&
            !this.isEnabled &&
            optInStatus === JackpotBetsOptStatusType.OptIn;

        if (shouldOptOut) {
            await this._updateOptInStatus(JackpotBetsOptStatusType.OptOut, true);
        }
    }

    async toggleJackpotBet(): Promise<void> {
        if (this.optInStatus === JackpotBetsOptStatusType.OptIn) {
            await this._updateOptInStatus(JackpotBetsOptStatusType.OptOut, false);
        } else {
            const newStatus = await this._showOptInJackpotBetDialog();
            await this._updateOptInStatus(newStatus, false);
        }
    }

    public async requestOptInDialog(): Promise<void> {
        if (!this.isEnabled) {
            return;
        }

        const status = await this._showOptInJackpotBetDialog();

        await this._updateOptInStatus(status, false);
    }

    private _broadcastOptInStatusChanged(newStatus: JackpotBetsOptStatusType): void {
        try {
            const broadcastMessage: JackpotBetsBroadcastMessage = {
                type: "opt-in-status-changed",
                payload: {
                    optInStatus: newStatus
                }
            }
            this._broadcastChannel.sendMessage(broadcastMessage);
        } catch (err) {
            this.services.logger.error('Failed to broadcast opt-in status change', err);
        }

    }

    private async _updateOptInStatus(newStatus: JackpotBetsOptStatusType, fromLobby: boolean): Promise<void> {
        if (this.optInStatus === newStatus) {
            return;
        }

        try {
            if (newStatus === JackpotBetsOptStatusType.OptIn) {
                await this.services.api.jackpotBetsController.optInToJackpotBets();
            }
            else {
                await this.services.api.jackpotBetsController.optOutFromJackpotBets({FromLobby: fromLobby});
            }

            this.optInStatus = newStatus;

            this._broadcastOptInStatusChanged(newStatus);

        } catch (err) {

            this.sendJackpotBetTechnicalEvent("update_opt_in_status_failed", `New status:  ${newStatus}`, JSON.stringify(err));

            if (this.isEnabled) {
                this.services.dialog.showSimpleTextModal({
                    text: 'Jackpot Drop is currently unavailable. Please try again later.',
                    buttons: [
                        {
                            buttonPreset: 'primary',
                            text: 'OK',
                            onClick: async () => {
                                return DialogButtonClickHandleResultEnum.CloseDialogAndContinue
                            }
                        }
                    ]
                })
            }

            this.services.logger.error(`Failed to update jackpot bets status to ${newStatus}`, err);
        }
    }

    private _showOptInJackpotBetDialog(): Promise<JackpotBetsOptStatusType> {
        return new Promise<JackpotBetsOptStatusType>(resolve => {
            this.services.dialog.showSheetModal(dialogHandler => (
                <JackpotBetsOptInDialogComponent
                    optIn={() => {
                        resolve(JackpotBetsOptStatusType.OptIn);
                        this.sendJackpotBetEvent('opt_in_popup_opt_in_click',
                            `curr_optin_status_${this.optInStatus}`, `new_optin_status_${JackpotBetsOptStatusType.OptIn}`);
                        dialogHandler.close();
                    }}
                    optOut={() => {
                        resolve(JackpotBetsOptStatusType.OptOut);
                        this.sendJackpotBetEvent('opt_in_popup_opt_in_click',
                            `curr_optin_status_${this.optInStatus}`, `new_optin_status_${JackpotBetsOptStatusType.OptOut}`);
                        dialogHandler.close();
                    }}
                />), {
                title: this.services.translation.translate('JACKPOT_BET'),
                hideDialogHeader: true,
                noDefaultContentPadding: true,
                overflow: "hidden",
                hideBorders: true,
                forceCentralization: true,
                forceTransparentBackground: true
            });
            this.sendJackpotBetEvent('opt_in_popup_view',
                ``, `curr_optin_status_${this.optInStatus}`);
        })
    }

    sendJackpotBetEvent(action: string, label?: string, value?: any): void {
        try {
            this.services.analytics.sendOrbitUserFlowEvent(JACKPOT_BET_ANALYTICS_CATEGORY, action, label, value);
        }
        catch (err) {
            console.log("Error on Orbit sendJackpotBetEvent : " + err);
        }
    }

    sendJackpotBetTechnicalEvent(action: string, label?: string, value?: any): void {
        try {
            this.services.analytics.sendOrbitUserFlowEvent(JACKPOT_BET_TECH_EVENTS_CATEGORY, action, label, value);
        }
        catch (err) {
            console.log("Error on Orbit sendJackpotBetEvent : " + err);
        }
    }
}
